/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

/**
 *
 * EmailForm
 *
 */

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { DetailHeader, InputField, SelectField, Button, Toast, DetailsPanelMessage, Detail, Modal } from 'cx-ui-components';
import gIcon from '../../../../assets/icons/g-icon.png';
import msIcon from '../../../../assets/icons/ms_icon.png';
import DetailWrapper from '../../../../components/DetailWrapper';
import styled from 'styled-components';
import {
  convertOauthResponseToJson,
  convertOauthStringToJson,
  msLogin,
} from "../../../../redux/modules/entities/integrations/utilities";

const WrappedDetailHeader = styled(DetailHeader)`
  margin-left: 35px;
`;

const GoogleLoginContainer = styled.div`
  display: flex;
  margin: 10px;
  padding: 5px;
`;

const GoogleLoginButton = styled(Button)`
  margin-left: 150px;
`;

const GoogleIcon = styled.img`
  height: 20px;
  width: 20px;
  margin-top: 2px;
  margin-bottom: 2px;
  margin-left: 2px;
  margin-right: 7px;
`;

const MsLoginContainer = styled.div`
  display: flex;
  margin: 10px;
  padding: 5px;
`;

const MsLoginButton = styled(Button)`
  margin-left: 150px;
`;

const MsIcon = styled.img`
  height: 16px;
  width: auto;
  margin-top: 2px;
  margin-bottom: 2px;
  margin-left: 2px;
  margin-right: 7px;
`;

const OauthCredentialId = styled.input``;

const PermissionsLink = styled.a`
  display: flex;
  flex-direction: column;
  margin-left: 25px;
  margin-bottom: 8px;
  text-decoration: underline;
  color: blue;
  cursor: pointer;
`;

const PermissionsModal = styled(Modal)`
  min-width: 300px;
  max-width: 500px;
  text-align: left;
  justify-content: left;
  background-color: #ebebeb;
  padding: 20px 0 10px;
`;

const PermissionsContent = styled.div`
  background-color: #ffffff;
  padding: 10px;
`;

const PermissionDescription = styled.div`
  margin: 5px;
  padding: 10px;
`;

const BtnsContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding-top: 10px;
`;

const CloseBtn = styled(Button)`
  width: 45%;
  border-radius: 4px;
  cursor: pointer;
  display: inline-block;
  font-size: 14px;
  padding: 3px 10px;
  position: relative;
  text-align: center;
  vertical-align: middle;
`;

export default class EmailForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      baseUrl: undefined,
      fromListener: false,
      googleButtonLabel: 'Sign In with Google',
      msNotificationVisible: false,
      oauthCredential: undefined,
      showPermissionsModal: false
    };
  }

  initGoogleApi = (nodeEnv) => {
    // load google api script
    if (nodeEnv !== 'test') {
      (function (d, s, id) {
        let js, fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) {
          return;
        }
        js = d.createElement(s);
        js.id = id;
        js.src = "https://accounts.google.com/gsi/client";
        js.type = "text/javascript";
        js.setAttribute('nonce', window.__cspNonce);
        fjs.parentNode.insertBefore(js, fjs);
      }(document, 'script', 'google-account-api'));
    }
  };

  googleLogin = (e) => {
    e.preventDefault();
    let googleClient;
    const self = this;

    googleClient = window.google.accounts.oauth2.initCodeClient({
      client_id: this.props.googleOauthClientId,
      scope: 'https://mail.google.com/',
      ux_mode: 'popup',
      callback: (codeResponse) => {
        if (codeResponse && codeResponse.code) {
          const body = {
            client_id: self.props.googleOauthClientId,
            client_secret: self.props.googleOauthClientSecret,
            code: codeResponse.code,
            grant_type: 'authorization_code',
            access_type: 'offline',
            redirect_uri: self.state.baseUrl
          };
          this.googleRequestToken(body, body.grant_type);
        } else if (codeResponse && codeResponse.error) {
          Toast.error('Sign In with Google failed. Please verify your credentials and that you granted the access.');
        }
      },
    });

    googleClient.requestCode();
  };

  googleRefreshToken = (e) => {
    e.preventDefault();
    if (this.state.oauthCredential) {
      const body = {
        client_id: this.props.googleOauthClientId,
        client_secret: this.props.googleOauthClientSecret,
        grant_type: 'refresh_token',
        refresh_token: this.state.oauthCredential.refreshToken
      };
      this.googleRequestToken(body, body.grant_type);
    }
  };

  googleRequestToken = (body, grantType) => {
    const self = this;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://oauth2.googleapis.com/token', true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onreadystatechange = function(response) { // Call a function when the state changes.
      if (this.readyState === XMLHttpRequest.DONE && this.status === 200) {
        if (response && response.target && response.target.response) {
          let responseObject = JSON.parse(response.target.response);
          if (grantType === 'authorization_code') {
            responseObject.tokenAcquiredTime = new Date().toISOString();
            self.props.setOAuthToken(JSON.stringify(responseObject));
            Toast.success('Google Access Token acquired successfully.');
          } else {
            responseObject.refreshToken = body.refresh_token;
            responseObject.tokenAcquiredTime = new Date().toISOString();
            self.props.setOAuthToken(JSON.stringify(responseObject));
            Toast.success('Google Access Token refreshed successfully.');
          }
        }
      } else {
        if (response && response.target && response.target.response) {
          let responseObject = JSON.parse(response.target.response);
          if (responseObject.error) {
            if (self.state.oauthCredential) {
              self.setState({ googleButtonLabel: 'Sign In with Google' });
              self.setState({ oauthCredential: undefined });
              self.props.setOAuthToken('');
              Toast.error(`Google Access Token process failed. (${responseObject.error}) ${responseObject.error_description}`);
            }
          }
        }
      }
    };
    xhr.send(JSON.stringify(body));
  };

  msLogin = (e) => {
    e.preventDefault();
    msLogin(this.state.baseUrl, this.props.msOauthClientId, this.props.msOauthClientSecret)
      .then((response) => {
        const msWindow = window.open(
          response,
          'cxengageMsWindow',
          'width=500,height=600'
        );
      })
      .catch((error) => {
        const errorMessage = error ? error : '';
        Toast.error(`Microsoft Access Token process failed. ${errorMessage}`);
      });
  };

  msRequestToken = (msLoginResponse) => {
    const self = this;
    const initialValues = this.props.initialValues.toJS();
    const body = {
      integrationId: initialValues.id,
      incomingType: this.props.incomingType,
      clientId: this.props.msOauthClientId,
      scope: 'https://outlook.office.com/IMAP.AccessAsUser.All https://outlook.office.com/POP.AccessAsUser.All https://outlook.office.com/SMTP.Send',
      redirectUri: this.state.baseUrl,
      code: msLoginResponse.code
    };

    // validation to avoid weird issue of duplicate success/error notification
    if (!this.state.msNotificationVisible) {
      // request for ms access token
      this.props.fetchOAuthAccessToken(body, this.state.fromListener);
    }
  };

  incomingTypeChange = (e) => {
    // uncomment this validation when we enable Google oauth
    // if (this.props.incomingType !== e.target.value) {
    //   this.props.setOAuthToken('');
    //   this.props.setSmtpUser('');
    // }
    if (e.target.value === 'oauth-google') {
      this.props.setImapHost('imap.gmail.com');
      this.props.setSmtpHost('smtp.gmail.com');
    } else if (e.target.value === 'oauth-ms') {
      this.props.setImapHost('outlook.office365.com');
      this.props.setSmtpHost('smtp.office365.com');
    } else {
      this.props.setImapHost('');
      this.props.setSmtpHost('');
    }
  };

  componentDidMount() {
    window.addEventListener('message', this.handleMessageEvent);

    let baseUrl = window.location.origin;
    const relativeUrl = window.location.href;
    if (baseUrl.indexOf('frontend-prs') > 0) {
      baseUrl = relativeUrl.substring(0, relativeUrl.indexOf('#'));
    }
    this.setState({ baseUrl });

    this.initGoogleApi(process.env.NODE_ENV);
    if (this.props.oauthCredential) {
      if (typeof this.props.oauthCredential === 'string') {
        const oauthObject = convertOauthStringToJson(this.props.oauthCredential);
        this.setState({ oauthCredential: oauthObject });
      } else if (typeof this.props.oauthCredential === 'object') {
        const oauthObject = this.props.oauthCredential.toJS();
        this.setState({ oauthCredential: oauthObject });
      }
      // this.setState({ googleButtonLabel: 'Refresh Google Access Token' });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessageEvent);
  }

  handleMessageEvent = (event) => {
    if (event.data && event.data.fromListener) {
      this.setState({ fromListener: event.data.fromListener.value });
    }
    if (event.data && (event.data.code || event.data.error)) {
      this.setState({ msNotificationVisible: false });
      if (event.data.code) {
        this.msRequestToken(event.data);
      } else if (event.data.error && !event.data.error.context) {
        if (!this.state.msNotificationVisible) {
          const errorMessage = event.data.error_description ? event.data.error_description : '';
          Toast.error(`Microsoft Access Token process failed. ${errorMessage}`);
          this.setState({ msNotificationVisible: true });
        }
      }
    }
  };

  render() {
    return (
      <DetailWrapper open data-automation="emailPropertiesSVG">
        <WrappedDetailHeader text="Properties"/>
        <SelectField
          name="properties.incomingType"
          label="Incoming Protocol *"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
          options={[
            {value: 'imap', label: 'Imap'},
            // {value: 'oauth-google', label: 'Google Oauth'},
            {value: 'oauth-ms', label: 'Microsoft Oauth'}
          ]}
          data-automation="integrationsIncomingType"
          onChange={(e) => this.incomingTypeChange(e)}
          required
        />
        {this.props.incomingType === 'oauth-google' && (
          <>
            <GoogleLoginContainer>
              <GoogleLoginButton
                onClick={(e) => this.googleLogin(e)}
              >
                <GoogleIcon
                  src={gIcon}
                />
                Sign In with Google
              </GoogleLoginButton>
              {/*<GoogleLoginButton*/}
              {/*  onClick={(e) => this.googleRefreshToken(e)}*/}
              {/*>*/}
              {/*  <GoogleIcon*/}
              {/*    src={gIcon}*/}
              {/*  />*/}
              {/*  Refresh Google Access Token*/}
              {/*</GoogleLoginButton>*/}
            </GoogleLoginContainer>
            <InputField
              name="properties.oauthCredential"
              label="Google Access Token *"
              componentType="input"
              dataType="password"
              inputType="text"
              placeholder="Google Oauth Credential..."
              data-automation="oauthCredentialInput"
              disabled
              maskValue
            />
          </>
        )}
        {this.props.incomingType === 'oauth-ms' && (
          <>
            <MsLoginContainer>
              <MsLoginButton
                onClick={(e) => this.msLogin(e)}
                disabled={this.props.selectedEntity && this.props.selectedEntity.get('active')}
              >
                <MsIcon
                  src={msIcon}
                />
                Sign In with Microsoft
              </MsLoginButton>
            </MsLoginContainer>
            <PermissionsLink onClick={() => this.setState({ showPermissionsModal: true })}>By signing in with Microsoft you are approving these permissions</PermissionsLink>
            <Detail label="Access Token generated by: " value={this.props.msOauthPreferredUsername} />
            <InputField
              name="properties.oauthCredential"
              label="Microsoft Access Token *"
              componentType="input"
              dataType="password"
              inputType="text"
              placeholder="Microsoft Oauth Credential..."
              data-automation="oauthCredentialInput"
              disabled
              maskValue
            />
            <OauthCredentialId
              name="properties.oauthCredentialId"
              type="hidden"
              data-automation="oauthCredentialIdInput"
            />
          </>
        )}
        <InputField
          name="properties.imapServer"
          label="Incoming IMAP Host *"
          componentType="input"
          inputType="text"
          data-automation="integrationsImapServer"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
        />
        <InputField
          name="properties.smtpHost"
          label="SMTP Host *"
          componentType="input"
          inputType="text"
          data-automation="integrationsSmtpHost"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
        />
        <InputField
          name="properties.smtpPort"
          label="SMTP Port *"
          componentType="input"
          inputType="text"
          data-automation="integrationsSmtpPort"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
          dataType="number"
        />
        <SelectField
          name="properties.smtpEncryptionType"
          label="SMTP Encryption *"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
          options={[{value: 'ssl/tls', label: 'ssl/tls'}, {value: 'starttls', label: 'starttls'}]}
          data-automation="integrationsSmtpEncryptionType"
          required
        />
        <InputField
          name="properties.smtpUser"
          label="SMTP User *"
          componentType="input"
          inputType="text"
          data-automation="integrationsSmtpUser"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission || (this.props.incomingType !== 'imap')}
        />
        {(this.props.incomingType !== 'oauth-google' && this.props.incomingType !== 'oauth-ms') && (
          <InputField
            name="properties.smtpPassword"
            label="SMTP Password *"
            componentType="input"
            data-automation="integrationsSmtpPassword"
            disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
            dataType="password"
            inputType="text"
            maskValue
            autoComplete={'new-password'}
          />
        )}
        {(this.props.initialValues && this.props.incomingType === 'oauth-google') &&
          <DetailsPanelMessage
            text={`The email and Google Access token should not have to be updated again. Only modify these fields when necessary. If you update the email, Sign In with Google using the new account.`}
            type="warning"
          />
        }
        {(this.props.initialValues && this.props.incomingType === 'oauth-ms' &&
            (this.props.msOauthPreferredUsername !== this.props.currentEmail)) &&
          <DetailsPanelMessage
            text={`The email used to generate the Microsoft Access token it is different than the SMTP User.`}
            type="error"
          />
        }
        {(this.props.initialValues && this.props.incomingType === 'oauth-ms') &&
          <DetailsPanelMessage
            text={`The email and Microsoft Access token should not have to be updated again. Only modify these fields when necessary. If you update the email, Sign In with Microsoft using the new account.`}
            type="warning"
          />
        }
        {this.state.showPermissionsModal &&
          <PermissionsModal>
            <PermissionsContent>
              <div>This app requires your approval to:</div>
              <DetailWrapper open={false}>
                <WrappedDetailHeader text="Maintain access to data you have given it access to"/>
                <PermissionDescription>
                  Allows the app to see and update the data you gave it access to, even when users are not currently using the app. This does not give the app any additional permissions.
                </PermissionDescription>
              </DetailWrapper>
              <DetailWrapper open={false}>
                <WrappedDetailHeader text="View users' basic profile"/>
                <PermissionDescription>
                  Allows the app to see your users' basic profile (e.g., name, picture, user name, email address).
                </PermissionDescription>
              </DetailWrapper>
              <DetailWrapper open={false}>
                <WrappedDetailHeader text="Read and write access to mailboxes via IMAP"/>
                <PermissionDescription>
                  Allows the app to have the same access to mailboxes as the signed-in user via IMAP protocol.
                </PermissionDescription>
              </DetailWrapper>
              <DetailWrapper open={false}>
                <WrappedDetailHeader text="Read and write access to mailboxes via POP"/>
                <PermissionDescription>
                  Allows the app to have the same access to mailboxes as the signed-in user via POP protocol.
                </PermissionDescription>
              </DetailWrapper>
              <DetailWrapper open={false}>
                <WrappedDetailHeader text="Send emails from mailboxes using SMTP AUTH"/>
                <PermissionDescription>
                  Allows the app to be able to send emails from the user's mailbox using the SMTP AUTH client submission protocol.
                </PermissionDescription>
              </DetailWrapper>
              <PermissionDescription>
                References:
                <PermissionsLink
                  href="https://learn.microsoft.com/en-us/graph/permissions-reference#openid-connect-oidc-scopes"
                  target="_blank">
                  OpenID Connect (OIDC) scopes
                </PermissionsLink>
                <PermissionsLink
                  href="https://learn.microsoft.com/en-us/graph/permissions-reference#mail-permissions"
                  target="_blank">
                  Mail permissions
                </PermissionsLink>
              </PermissionDescription>
            </PermissionsContent>
            <BtnsContainer>
              <CloseBtn data-automation="confirmButton" onClick={() => this.setState({ showPermissionsModal: false })} buttonType="primary">
                Close
              </CloseBtn>
            </BtnsContainer>
          </PermissionsModal>
        }
      </DetailWrapper>
    );
  }
}

EmailForm.propTypes = {
  currentEmail: PropTypes.string,
  dirty: PropTypes.bool,
  fetchOAuthAccessToken: PropTypes.func,
  googleOauthClientId: PropTypes.string,
  googleOauthClientSecret: PropTypes.string,
  incomingType: PropTypes.string,
  inherited: PropTypes.bool,
  initialValues: PropTypes.object,
  isSaving: PropTypes.bool,
  msOauthClientId: PropTypes.string,
  msOauthClientSecret: PropTypes.string,
  msOauthPreferredUsername: PropTypes.string,
  oauthCredential: PropTypes.any,
  oauthCredentialId: PropTypes.string,
  oauthCredentialResponse: PropTypes.any,
  selectedEntity: PropTypes.object,
  setImapHost: PropTypes.func,
  setOAuthCredentialId: PropTypes.func,
  setOAuthToken: PropTypes.func,
  setSmtpHost: PropTypes.func,
  setSmtpUser: PropTypes.func,
  userHasUpdatePermission: PropTypes.bool,
};
